import { CartLinesUpdateEvent } from '@shopify/events';

// Adds a "Product name — Add to cart" bar under each Instafeed post (blocks/instafeed-shop.liquid).
// Instafeed renders its cards client-side from api.nfcube.com/feed; we read that same response to learn
// which post (ID + caption) each card shows, then look the product up in the mapping.
const root = window.Shopify?.routes?.root ?? '/';
// theme's assets/icon-add-to-cart.svg (bag +) and a check for the "added" moment
const BAG_PLUS =
  '<svg aria-hidden="true" viewBox="0 0 20 20" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M16.608 9.421V6.906H3.392v8.016c0 .567.224 1.112.624 1.513.4.402.941.627 1.506.627H8.63M8.818 3h2.333c.618 0 1.212.247 1.649.686a2.35 2.35 0 0 1 .683 1.658v1.562H6.486V5.344c0-.622.246-1.218.683-1.658A2.33 2.33 0 0 1 8.82 3"/><path stroke="currentColor" stroke-linecap="round" stroke-width="1.5" d="M14.608 12.563v5m2.5-2.5h-5"/></svg>';
const CHECK =
  '<svg aria-hidden="true" viewBox="0 0 20 20" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M4.5 10.5l3.5 3.5 7.5-8"/></svg>';
const products = new Map(); // handle -> Promise<product json>

const mapping = () => {
  const map = {};
  document.querySelectorAll('[data-instafeed-shop-map]').forEach((el) => {
    try {
      Object.assign(map, JSON.parse(el.textContent));
    } catch {}
  });
  return map;
};

const productFor = (post, map) => {
  if (map[post.id]) return map[post.id];
  const caption = (post.caption?.text || '').toLowerCase();
  const word = Object.keys(map).find((key) => !/^\d+$/.test(key) && caption.includes(key));
  return word ? map[word] : null;
};

const fetchProduct = (handle) => {
  if (!products.has(handle)) {
    products.set(handle, fetch(`${root}products/${handle}.js`).then((r) => (r.ok ? r.json() : null)).catch(() => null));
  }
  return products.get(handle);
};

const money = (cents) =>
  new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
    style: 'currency',
    currency: window.Shopify?.currency?.active || 'INR',
  }).format(cents / 100);

// The feed URL (with its hash) is only known from Instafeed's own request; watch for it.
let feedUrl = null;
let feed = null;
new PerformanceObserver((list) => {
  const entry = list.getEntries().find((e) => e.name.includes('api.nfcube.com/feed'));
  if (entry && !feedUrl) {
    feedUrl = entry.name;
    decorate();
  }
}).observe({ type: 'resource', buffered: true });

const loadFeed = async () => {
  if (!feedUrl) return null;
  feed ??= fetch(feedUrl)
    .then((r) => r.json())
    .then((j) => j.data || [])
    .catch(() => null);
  const posts = await feed;
  if (!posts) feed = null; // let a later run retry
  return posts;
};

const addToCart = async (button, variantId) => {
  button.disabled = true;
  button.classList.add('is-loading');
  const deferred = CartLinesUpdateEvent.createPromise();
  button.dispatchEvent(
    new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: [{ merchandiseId: variantId, quantity: 1 }], promise: deferred.promise })
  );
  try {
    const res = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1, sections: 'cart-drawer-section', sections_url: location.pathname }),
    });
    const added = await res.json();
    if (added.status) throw new Error(added.description || added.message);
    const cart = await (await fetch(`${root}cart.js`)).json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'instafeed-shop', sourceId: 'instafeed-shop', itemCount: 1, sections: added.sections, didError: false },
    });
    button.innerHTML = CHECK;
  } catch (error) {
    deferred.reject(error);
  }
  button.classList.remove('is-loading');
  setTimeout(() => {
    button.disabled = false;
    button.innerHTML = BAG_PLUS;
  }, 1500);
};

const decorate = async () => {
  if (!document.querySelector('.instafeed-shoppable__card:not([data-shop-bar])')) return;
  const posts = await loadFeed();
  if (!posts?.length) return; // feed not known yet: the PerformanceObserver calls us again
  const cards = [...document.querySelectorAll('.instafeed-shoppable__card')].filter((c) => !c.dataset.shopBar);
  cards.forEach((c) => (c.dataset.shopBar = '1')); // claim them now so overlapping runs don't double up
  const map = mapping();
  const all = [...document.querySelectorAll('.instafeed-shoppable__card')];

  for (const card of cards) {
    // cards render in feed order; a carousel may repeat them, hence the modulo
    const post = posts[all.indexOf(card) % posts.length];
    const handle = post && productFor(post, map);
    const product = handle && (await fetchProduct(handle));
    if (!product) continue;
    const variant = product.variants.find((v) => v.available) || product.variants[0];

    const bar = document.createElement('div');
    bar.className = 'instafeed-shop-bar';
    bar.innerHTML = `<a class="instafeed-shop-bar__link" href="${product.url}"></a>`;
    const link = bar.firstElementChild;
    link.textContent = product.title;
    const price = document.createElement('span');
    price.className = 'instafeed-shop-bar__price';
    price.textContent = money(variant.price);
    bar.append(price); // name on the left, price on the right
    // Add to cart sits on the post itself, bottom-right
    const add = document.createElement('button');
    add.type = 'button';
    add.className = 'instafeed-shop-bar__add';
    add.innerHTML = BAG_PLUS;
    add.setAttribute('aria-label', `Add ${product.title} to cart`);
    if (!variant.available) {
      add.disabled = true;
      add.setAttribute('aria-label', `${product.title} is sold out`);
    }
    // keep the app's own click (popup) from firing on our controls
    bar.addEventListener('click', (e) => e.stopPropagation());
    add.addEventListener('click', (e) => e.stopPropagation());
    add.addEventListener('click', () => addToCart(add, variant.id));
    (card.querySelector('.instafeed-shoppable__frame') || card).append(add);
    (card.querySelector('.instafeed-shoppable__card-inner') || card).append(bar);
  }
};

let queued = false;
new MutationObserver(() => {
  if (queued) return;
  queued = true;
  requestAnimationFrame(() => {
    queued = false;
    decorate();
  });
}).observe(document.body, { childList: true, subtree: true });
decorate();

